import { format, addDays, subDays, getDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { Menu, MenuShort } from "~/entities/menuEntities";
import { DaysOfWeek, TimeOfDay } from "~/entities/siteEntities";
import uniqBy from "lodash/uniqBy";

const createDate = (baseDate: Date, timeOfDay: TimeOfDay): Date => {
  const date = new Date(baseDate);
  date.setHours(timeOfDay.hour, timeOfDay.minute, 0, 0);
  return date;
};

const isTimeInRange = (
  currentTime: Date,
  startsAt: Date,
  endsAt: Date,
): boolean => {
  return currentTime >= startsAt && currentTime < endsAt;
};

const isDayApplicable = (days: DaysOfWeek, day: number): boolean => {
  switch (day) {
    case 0:
      return days.sunday;
    case 1:
      return days.monday;
    case 2:
      return days.tuesday;
    case 3:
      return days.wednesday;
    case 4:
      return days.thursday;
    case 5:
      return days.friday;
    case 6:
      return days.saturday;
    default:
      return false;
  }
};

export const isMenuAvailable = (
  menu: Menu | MenuShort,
  currentTime: Date,
): boolean => {
  if (!menu || !menu.availability.length) return false;

  for (const availability of menu.availability) {
    // If start or end time is nil, the menu is invalid
    if (!availability.startsAt || !availability.endsAt) {
      return false;
    }

    const startTime = availability.startsAt;
    const endTime = availability.endsAt;

    const startDate = createDate(currentTime, startTime);
    const endDate = createDate(currentTime, endTime);

    let availabilityFromPrevDayOfWeek = false;
    // Check if the end time is the same or before the start time
    // If it is then the end time refers to that time the next day
    if (
      endTime.hour < startTime.hour ||
      (endTime.hour === startTime.hour && endTime.minute <= startTime.minute)
    ) {
      // Okay in this case we allow current time to be < end time or >= start time
      // In the case that it is < end time, we are using availability from the previous day,
      // This means the day check needs to check if the previous day matches
      if (currentTime < endDate) {
        // We have met the time requirement. Record that the day of the week requirement is for prev day.
        availabilityFromPrevDayOfWeek = true;
      } else if (currentTime < startDate) {
        // This code path occurs if endDate <= currentTime < startDate which is possible since endTime <= startTime
        // This is also the only condition where the time requirement is not satisfied for endTime <= startTime
        continue;
      }
    } else if (!isTimeInRange(currentTime, startDate, endDate)) {
      // In this case start time < endTime, and the current time doesn't fit in that range
      // So simply return say not available
      continue;
    }

    // Check if the menu is applicable for the current day
    const dayOfWeek = availabilityFromPrevDayOfWeek
      ? getDay(subDays(currentTime, 1))
      : getDay(currentTime);

    if (isDayApplicable(availability.applies, dayOfWeek)) {
      return true;
    }
  }

  return false;
};

// Wrapper function that handles timezone conversion
export const isMenuOpen = (
  menu: Menu | MenuShort,
  requestedReadyTime?: string | null | undefined,
  timeZone?: string,
): boolean => {
  const readyTime = requestedReadyTime ?? new Date();
  if (!menu.availability.length) return false;
  const zonedReadyTime = timeZone
    ? toZonedTime(readyTime, timeZone)
    : new Date(readyTime);
  return isMenuAvailable(menu, zonedReadyTime);
};

// Helper function to get next available time with timezone support
export const getNextAvailableTime = (
  menu: Menu,
  timeZone: string = "UTC",
): string => {
  if (!menu.availability.length) return "not available";

  const currentTime = toZonedTime(new Date(), timeZone);

  for (const availability of menu.availability) {
    if (!availability.startsAt || !availability.endsAt) continue;
    if (!isDayApplicable(availability.applies, getDay(currentTime))) continue;

    const startDate = createDate(currentTime, availability.startsAt);
    if (startDate <= currentTime) continue;

    return `today at ${format(startDate, "h:mm a")}`;
  }

  // If not available today, check tomorrow
  const tomorrow = addDays(currentTime, 1);
  for (const availability of menu.availability) {
    if (!availability.startsAt || !availability.endsAt) continue;
    if (!isDayApplicable(availability.applies, getDay(tomorrow))) continue;

    const startDate = createDate(tomorrow, availability.startsAt);
    return `tomorrow at ${format(startDate, "h:mm a")}`;
  }

  return "not available";
};

export const getActiveMenu = (
  menus: MenuShort[],
  requestedReadyTime?: string | null | undefined,
  timeZone?: string,
) => {
  return menus.length
    ? (menus.find((menu) => isMenuOpen(menu, requestedReadyTime, timeZone)) ??
        menus[0])
    : null;
};

export const getProductsList = (menu: Menu) =>
  uniqBy(
    menu?.tabs.flatMap((tab) => tab.items),
    (x) => x.productCode,
  );
